/**
 * Where a world sound sits for the listener (the player, whom the camera follows): stereo pan from
 * its x on screen, and its distance in tiles for the engine's attenuation (sfx/engine.ts).
 */
import type { World } from '@nine/shared';
import type { IsoCamera } from '../render/camera';
import type { SfxOpts } from './registry';

/** What the placement reads: the world (the player) and the camera (screen x). */
export interface SfxView {
  readonly world: World;
  readonly iso: IsoCamera;
}

/** Screen pixels (world scale) from the centre to a full pan. */
export const PAN_SPAN = 230;
/** Never quite all the way to one ear. */
const PAN_MAX = 0.85;

/** Pan and distance of a point (tile coordinates). */
export function placeAt(v: SfxView, x: number, y: number): Pick<SfxOpts, 'pan' | 'dist'> {
  const p = v.world.player;
  const dist = Math.hypot(x - p.x, y - p.y);
  const [sx] = v.iso.proj(x, y);
  const [px] = v.iso.proj(p.x, p.y);
  const pan = Math.max(-PAN_MAX, Math.min(PAN_MAX, (sx - px) / PAN_SPAN));
  return { pan, dist };
}
